<template>
  <div class="mobile-kb-menu lg:tw-hidden">
    <div
      class="tw-fixed tw-left-0 tw-right-0 tw-top-[64px] tw-z-[94] tw-h-screen tw-bg-white tw-p-4 tw-transition-all tw-delay-[50ms] tw-duration-300"
      :class="{
        'tw-translate-x-0': isOpen,
        'tw-translate-x-full': !isOpen,
      }"
    >
      <div class="tw-mb-4 tw-flex tw-items-center tw-justify-between">
        <h4 class="tw-m-0">Categorieën</h4>
        <div class="tw-flex tw-items-center tw-gap-2">
          <c-button-icon
            v-if="isWebmaster"
            :icon="toClassicIcon(settingsIcon)"
            text-color="dark"
            flat
          >
            <c-menu auto-close>
              <q-list style="min-width: 200px">
                <q-item
                  v-for="(item, index) in menuItems"
                  :key="index"
                  clickable
                  @click="item.onClick"
                >
                  <div class="tw-flex tw-items-center tw-gap-4">
                    <q-icon size="12px" :name="toClassicIcon(item.icon)" />
                    <div>{{ item.label }}</div>
                  </div>
                </q-item>
              </q-list>
            </c-menu>
          </c-button-icon>
          <c-button-icon outline icon="fas fa-times" fab @click="close" />
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <c-banner v-if="!items.length" type="warning"
          >Er zijn geen categorieën gevonden</c-banner
        >
        <div
          v-for="category in items"
          :key="category.key"
          class="tw-flex tw-items-start tw-gap-1"
        >
          <menu-link
            :item="category"
            is-child
            class="font-weight-normal tw-min-w-0 tw-flex-1"
          />
          <c-button-icon
            v-if="isWebmaster"
            size="sm"
            flat
            text-color="dark"
            :icon="toClassicIcon(verticalMenuIcon)"
            class="tw-h-10 tw-shrink-0"
          >
            <c-menu auto-close>
              <q-list style="min-width: 180px">
                <q-item clickable @click="openEditDialog(category.key)">
                  <div class="tw-flex tw-items-center tw-gap-3">
                    <q-icon size="12px" :name="toClassicIcon(editIcon)" />
                    <div>Bewerken</div>
                  </div>
                </q-item>
                <q-item clickable @click="openDeleteDialog(category.key)">
                  <div
                    class="tw-flex tw-items-center tw-gap-3 tw-text-negative"
                  >
                    <q-icon size="12px" :name="toClassicIcon(deleteIcon)" />
                    <div>Verwijderen</div>
                  </div>
                </q-item>
              </q-list>
            </c-menu>
          </c-button-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  deleteIcon,
  editIcon,
  settingsIcon,
  toClassicIcon,
  verticalMenuIcon,
} from "common/defaultIcons";
import MenuLink from "components/layout/MenuLink.vue";
import { useKnowledgeBaseCategoryActions } from "composables/knowledge-base/useKnowledgeBaseCategoryActions";
import { checkRole } from "composables/useHelpers";
import type { MenuItem } from "models/menu/menu";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

interface Props {
  items: MenuItem[];
}

defineProps<Props>();

const route = useRoute();
const isOpen = ref(false);

const isWebmaster = computed(() => checkRole("webmaster"));

const { menuItems, openEditDialog, openDeleteDialog } =
  useKnowledgeBaseCategoryActions();

const open = () => {
  isOpen.value = true;
  document.body.classList.add("tw-overflow-hidden");
};

const close = () => {
  isOpen.value = false;
  document.body.classList.remove("tw-overflow-hidden");
};

watch(route, close);

onMounted(() => {
  window.addEventListener("beforeunload", close);
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeunload", close);
  document.body.classList.remove("tw-overflow-hidden");
});

defineExpose({ open, close });
</script>

<style scoped></style>
